import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  placedStage,
  scoreStage,
  type AdminChildProfile,
  type CheckStage,
  type StageMarks,
} from '@qq/shared';
import { api } from '../lib/api.js';

/**
 * Where a child is in phonics.
 *
 * Two ways in. A parent who knows - "she reads all of stage 8" - sets the
 * stage. Otherwise, the check with a grown-up: the child sees one big sound
 * or word at a time and says it; the grown-up marks it on their side of the
 * screen. The child never sees a mark, a count or the word "test".
 */
export function AdminPhonics() {
  const { childId = '' } = useParams();
  const child = useQuery({
    queryKey: ['admin', 'children', childId],
    queryFn: () => api<AdminChildProfile>(`/admin/children/${childId}`),
  });
  const stages = useQuery({
    queryKey: ['admin', 'phonics-stages'],
    queryFn: () => api<{ stages: CheckStage[] }>('/admin/phonics-stages'),
  });
  const [mode, setMode] = useState<'home' | 'check' | 'done'>('home');
  const [chosen, setChosen] = useState(1);
  const [result, setResult] = useState<number | null>(null);

  const place = useMutation({
    mutationFn: (stage: number) =>
      api<{ stage: number; placed: number }>(`/admin/children/${childId}/phonics-placement`, {
        method: 'POST',
        body: { stage },
      }),
  });

  const name = child.data?.nickname ?? 'your child';
  if (!stages.data) return <main className="admin">Loading…</main>;

  if (mode === 'check') {
    return (
      <PhonicsCheck
        name={name}
        stages={stages.data.stages}
        onFinish={(stage) => {
          setResult(stage);
          setMode('done');
        }}
        onStop={() => setMode('home')}
      />
    );
  }

  return (
    <main className="admin">
      <Link to="/admin/children">&larr; Children</Link>
      <h1>{name}&rsquo;s phonics</h1>

      {place.isSuccess ? (
        <p className="praise-note">
          Done. {name} now starts from stage {place.data.stage}: {place.data.placed} reading skills
          were taken as known, and the next ones will start appearing in missions.
        </p>
      ) : mode === 'done' && result !== null ? (
        <section className="card">
          <h2>The check is finished</h2>
          {result === 0 ? (
            <p>
              Stage 1 is still being learnt, so nothing changes - {name} carries on from the start.
            </p>
          ) : (
            <>
              <p>
                {name} is at <strong>stage {result}</strong>. Placing them there takes the sounds
                and tricky words up to stage {result} as known; real answers will correct it if it
                is not quite right.
              </p>
              <button type="button" className="btn" onClick={() => place.mutate(result)}>
                Place {name} at stage {result}
              </button>
            </>
          )}
        </section>
      ) : (
        <>
          <section className="card">
            <h2>Do the check together</h2>
            <p className="muted">
              Sit with {name}. One sound or word appears at a time; {name} says it, and you mark it.
              It stops on its own at the first stage that is not secure yet - a few minutes.
            </p>
            <button type="button" className="btn btn--big" onClick={() => setMode('check')}>
              Start the check
            </button>
          </section>

          <section className="card">
            <h2>Or set the stage yourself</h2>
            <p className="muted">
              If you already know - from school or from hearing them read - choose the highest stage
              whose sounds and tricky words they know well.
            </p>
            <label>
              Stage
              <select value={chosen} onChange={(e) => setChosen(Number(e.target.value))}>
                {stages.data.stages.map((s) => (
                  <option key={s.stage} value={s.stage}>
                    {s.stage}: {s.graphemes.join(' ')}
                  </option>
                ))}
              </select>
            </label>
            <button type="button" className="btn" onClick={() => place.mutate(chosen)}>
              Place {name} at stage {chosen}
            </button>
          </section>
        </>
      )}
      {place.isError && <p className="error-note">That did not save. Try again.</p>}
    </main>
  );
}

/**
 * The check itself. The top of the screen is the child's: one big sound or
 * word. The bottom bar is the grown-up's.
 */
export function PhonicsCheck({
  name,
  stages,
  onFinish,
  onStop,
}: {
  name: string;
  stages: CheckStage[];
  onFinish: (stage: number) => void;
  onStop: () => void;
}) {
  const [stageIndex, setStageIndex] = useState(0);
  const [itemIndex, setItemIndex] = useState(0);
  const [marks, setMarks] = useState<StageMarks[]>([]);

  const stage = stages[stageIndex]!;
  const items = [...stage.graphemes, ...stage.trickyWords];
  const shown = items[itemIndex]!;

  const mark = (right: boolean) => {
    const current = marks[stageIndex] ?? { graphemes: [], tricky: [] };
    const next: StageMarks =
      itemIndex < stage.graphemes.length
        ? { ...current, graphemes: [...current.graphemes, right] }
        : { ...current, tricky: [...current.tricky, right] };
    const all = [...marks.slice(0, stageIndex), next];
    setMarks(all);

    if (itemIndex + 1 < items.length) {
      setItemIndex(itemIndex + 1);
      return;
    }
    // The stage is done. Not passed, or the last stage: that is the answer.
    if (!scoreStage(stage, next).passed || stageIndex + 1 === stages.length) {
      onFinish(placedStage(stages, all));
      return;
    }
    setStageIndex(stageIndex + 1);
    setItemIndex(0);
  };

  return (
    <main className="phonics-check">
      <div className="phonics-check__child" aria-live="polite">
        {shown}
      </div>
      <div className="phonics-check__grown-up">
        <p className="muted">
          For the grown-up: did {name} say it? (Stage {stage.stage})
        </p>
        <div className="selfie-actions">
          <button type="button" className="btn" onClick={() => mark(true)}>
            ✓ Yes
          </button>
          <button type="button" className="btn btn--outline" onClick={() => mark(false)}>
            ✗ Not yet
          </button>
        </div>
        <button type="button" className="btn btn--quiet" onClick={onStop}>
          Stop the check
        </button>
      </div>
    </main>
  );
}
